/**
 * Memoized step item component for HistoryPanel.
 * Extracted from HistoryPanel to enable virtualization and reduce re-renders.
 */

import React, { cloneElement, memo } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslation } from '~/app';
import PlayerProfilePicture from '~/components/ProfilePicture/PlayerProfilePicture';
import { setSelectedStep } from '~/overlay/actions';
import type { SemanticStep } from '~/replay/eventCompression';
import { EventType } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

import { MiniCardIcon, MiniDiceGroup, MiniDiceIcon, MiniHorseIcon } from './icons';

export interface StepItemMetadata {
  step: SemanticStep;
  isActive: boolean;
  isLive: boolean;
  line1: string;
  line2: string;
  timeLabel: string;
  ariaLabel: string;
  stepIcon: React.ReactNode;
  horseColorHex: string | null;
  horseName: string | null;
  diceRollPlayerColorHex: string | null;
  playerIdForDisplay: string;
  /** Player for display (live: reducer Player; replay: full tmmodel Player; both have userId/userid + name). */
  player: { userId?: string; userid?: string; name?: string } | null;
  /** Resolved dice values for SELECTMOVEOPP dice moves (for icon when step.diceValues is empty) */
  diceValuesForDisplay?: number[] | undefined;
}

interface HistoryStepItemProps {
  metadata: StepItemMetadata;
  compact: boolean;
  itemIndex: number;
  onRef: (el: HTMLButtonElement | null, index: number) => void;
  /** When provided, called instead of dispatching setSelectedStep (for shared timeline / replay). */
  onSelectStep?: (step: SemanticStep) => void;
  /** When provided, show "Start game from here" and call with step.targetEventIndex (replay only). */
  onSpawnFromHere?: (eventIndex: number) => void;
}

export const HistoryStepItem: React.FC<HistoryStepItemProps> = memo(
  ({ metadata, compact, itemIndex, onRef, onSelectStep, onSpawnFromHere }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
      step,
      isActive,
      line1,
      line2,
      timeLabel,
      ariaLabel,
      stepIcon,
      horseColorHex,
      horseName,
      diceRollPlayerColorHex,
      playerIdForDisplay,
      player,
    } = metadata;

    const iconSize = compact ? 'w-6 h-6' : 'w-7 h-7';
    // Smaller size for single dice icon (matching grouped dice size)
    const singleDiceSize = iconSize.includes('w-7') ? 'w-4 h-4' : 'w-3.5 h-3.5';

    const handleRowClick = () => {
      if (onSelectStep) {
        onSelectStep(step);
      } else {
        dispatch(setSelectedStep(step.originalIndex));
      }
    };

    return (
      <div className="flex w-full flex-col gap-0 border-b border-amber-200/40 last:border-b-0">
        <button
          ref={(el) => onRef(el, itemIndex)}
          type="button"
          onClick={handleRowClick}
          aria-label={ariaLabel}
          className={cn(
            'relative flex w-full items-center gap-2 rounded text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
            compact ? 'px-2 py-1.5' : 'px-2 py-2',
            isActive
              ? 'bg-amber-500/20 outline-1 outline-offset-[-1px] outline-amber-500/50'
              : 'hover:bg-amber-500/10',
          )}
        >
          {/* Left slot: event-first icon (card, dice, horse, or system icon) */}
          <div className={cn('relative z-10 flex flex-shrink-0 flex-col items-center', iconSize)}>
            {step.entry.eventType === EventType.SELECTMOVEOPP &&
            (step.actionType === 'card' || step.actionType === 'dice') &&
            horseColorHex ? (
              step.actionType === 'card' ? (
                <span
                  title={step.handicapExceeded ? t('history_handicap_exceeded_tooltip') : undefined}
                >
                  <MiniCardIcon
                    value={step.actionValue ?? null}
                    color={horseColorHex}
                    className={iconSize}
                    handicapExceeded={step.handicapExceeded === true}
                  />
                </span>
              ) : (
                (() => {
                  const diceVals = metadata.diceValuesForDisplay ?? step.diceValues;
                  const showTwoDice = diceVals && diceVals.length >= 2;
                  return showTwoDice ? (
                    <MiniDiceGroup values={diceVals} color={horseColorHex} className={iconSize} />
                  ) : (
                    <MiniDiceIcon
                      value={(diceVals && diceVals[0]) ?? step.actionValue ?? null}
                      color={horseColorHex}
                      className={iconSize}
                    />
                  );
                })()
              )
            ) : step.entry.eventType === EventType.SELECTCARDDICE ? (
              <MiniCardIcon
                value={(() => {
                  const p = step.entry.payload as Record<string, unknown> | undefined;
                  const cardValue =
                    (p?.card as number | undefined) ??
                    (p?.cardValue as number | undefined) ??
                    (p?.value as number | undefined);
                  return typeof cardValue === 'number' ? cardValue : null;
                })()}
                color={diceRollPlayerColorHex || '#737373'}
                className={iconSize}
              />
            ) : (step.entry.eventType === EventType.SELECTDICES ||
                step.entry.eventType === EventType.SELECTDICESOPP) &&
              step.diceValues &&
              step.diceValues.length > 0 ? (
              step.diceValues.length > 1 ? (
                // Multiple dice: show diagonally grouped
                <MiniDiceGroup
                  values={step.diceValues}
                  color={diceRollPlayerColorHex || '#737373'}
                  className={iconSize}
                  selectedIndex={
                    step.entry.eventType === EventType.SELECTDICESOPP &&
                    step.diceSelectedIndex != null &&
                    step.diceSelectedIndex >= 0 &&
                    step.diceSelectedIndex < step.diceValues.length
                      ? step.diceSelectedIndex
                      : null
                  }
                />
              ) : (
                // Single die: show smaller (matching grouped dice size)
                <MiniDiceIcon
                  value={step.diceValues[0]}
                  color={diceRollPlayerColorHex || '#737373'}
                  className={singleDiceSize}
                  disabled={
                    step.entry.eventType === EventType.SELECTDICESOPP &&
                    step.diceSelectedIndex != null &&
                    step.diceSelectedIndex >= 0 &&
                    step.diceSelectedIndex < step.diceValues.length &&
                    step.diceSelectedIndex !== 0
                  }
                />
              )
            ) : step.entry.eventType === EventType.ADDBONUSCARDSOPP ? (
              <MiniCardIcon value={0} color={horseColorHex ?? '#b45309'} className={iconSize} />
            ) : (
              <div
                className={cn(
                  'flex flex-shrink-0 items-center justify-center rounded-full',
                  iconSize,
                  isActive
                    ? 'bg-amber-500 text-white'
                    : 'border border-amber-300 bg-amber-100 text-amber-600',
                )}
              >
                {cloneElement(
                  stepIcon as React.ReactElement<{ className?: string }>,
                  {
                    className: compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
                  } as React.Attributes & { className?: string },
                )}
              </div>
            )}
          </div>

          {/* Two-line content + time (time hidden in compact) */}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex min-w-0 items-center justify-between gap-1">
              <span
                className={cn(
                  'min-w-0 truncate text-xs font-medium',
                  isActive ? 'text-amber-900' : 'text-amber-800',
                )}
              >
                {line1}
              </span>
              {!compact && (
                <span className="flex-shrink-0 text-[10px] text-stone-400 tabular-nums">
                  {timeLabel}
                </span>
              )}
            </div>
            {line2 || (horseName && horseColorHex) ? (
              <span
                className={cn(
                  'flex min-w-0 items-center gap-1.5 truncate text-xs whitespace-nowrap text-amber-700/90',
                )}
              >
                {player && (
                  <>
                    <PlayerProfilePicture
                      userId={playerIdForDisplay}
                      username={player.name || ''}
                      size="sm"
                      className={cn(
                        'flex-shrink-0 overflow-hidden rounded-full',
                        'h-4 w-4',
                        isActive ? 'opacity-100 ring-1 ring-amber-500/30' : 'opacity-80',
                      )}
                    />
                    <span className="min-w-0 truncate">{player.name}</span>
                  </>
                )}
                {horseName && horseColorHex && (
                  <>
                    <MiniHorseIcon color={horseColorHex} className="h-4 w-4 flex-shrink-0" />
                    <span className="min-w-0 truncate">{horseName}</span>
                  </>
                )}
              </span>
            ) : null}
          </div>
        </button>
        {onSpawnFromHere && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSpawnFromHere(step.targetEventIndex);
            }}
            className="mt-0.5 ml-auto rounded px-2 text-[10px] text-amber-700 underline hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            aria-label={t('history_start_game_from_here')}
          >
            {t('history_start_game_from_here')}
          </button>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if metadata changed, compact prop changed, or itemIndex changed
    return (
      prevProps.itemIndex === nextProps.itemIndex &&
      prevProps.compact === nextProps.compact &&
      prevProps.onSelectStep === nextProps.onSelectStep &&
      prevProps.onSpawnFromHere === nextProps.onSpawnFromHere &&
      prevProps.metadata.step.originalIndex === nextProps.metadata.step.originalIndex &&
      prevProps.metadata.step.handicapExceeded === nextProps.metadata.step.handicapExceeded &&
      prevProps.metadata.isActive === nextProps.metadata.isActive &&
      prevProps.metadata.line1 === nextProps.metadata.line1 &&
      prevProps.metadata.line2 === nextProps.metadata.line2 &&
      prevProps.metadata.timeLabel === nextProps.metadata.timeLabel &&
      prevProps.metadata.horseColorHex === nextProps.metadata.horseColorHex &&
      prevProps.metadata.horseName === nextProps.metadata.horseName &&
      prevProps.metadata.diceRollPlayerColorHex === nextProps.metadata.diceRollPlayerColorHex &&
      prevProps.metadata.playerIdForDisplay === nextProps.metadata.playerIdForDisplay &&
      (prevProps.metadata.player?.name ?? null) === (nextProps.metadata.player?.name ?? null)
    );
  },
);

HistoryStepItem.displayName = 'HistoryStepItem';
